import { useCallback } from 'react';
import { endpoints } from '../api/client.js';
import { useResource } from '../hooks/useResource.js';
import PageHeader from '../components/PageHeader.jsx';
import { Panel } from '../components/Panel.jsx';
import { Resource } from '../components/StateViews.jsx';
import { countLines, formatNumber } from '../lib/format.js';

const BLOBS = [
  {
    key: 'sitesMap',
    title: 'sites.map',
    path: '/etc/haproxy/sites.map',
    subtitle: 'Domain → backend. HAProxy consults this first on every request.',
  },
  {
    key: 'tlsMap',
    title: 'tls.map',
    path: '/etc/haproxy/tls.map',
    subtitle: 'Domains with a certificate, so HAProxy knows what may terminate TLS.',
  },
  {
    key: 'backends',
    title: 'backends',
    path: '/etc/haproxy/conf.d/',
    subtitle: 'One backend per site, each pointing at a loopback port.',
  },
];

export default function Config() {
  const load = useCallback((signal) => endpoints.config(signal), []);
  const state = useResource(load, []);

  return (
    <div className="page">
      <PageHeader
        eyebrow="HAProxy"
        title="Generated config"
        lede="Rendered from the registry by liftedctl reconcile. Read-only. Editing these files by hand would be overwritten on the next reconcile."
        actions={
          <button type="button" className="btn" onClick={state.refresh}>
            Refresh
          </button>
        }
      />

      <Resource
        state={state}
        loadingLabel="Reading generated config…"
        loadingRows={4}
        errorTitle="Could not read the generated config"
        emptyTitle="No config returned"
        emptyBody="The host returned an empty configuration payload."
      >
        {(config) => (
          <div className="stack">
            {BLOBS.map((blob) => (
              <ConfigPane
                key={blob.key}
                title={blob.title}
                path={blob.path}
                subtitle={blob.subtitle}
                content={config[blob.key]}
              />
            ))}
          </div>
        )}
      </Resource>
    </div>
  );
}

function ConfigPane({ title, path, subtitle, content }) {
  const text = typeof content === 'string' ? content : '';
  const lines = countLines(text);

  return (
    <Panel
      title={<span className="mono">{title}</span>}
      subtitle={subtitle}
      footer={
        <span className="panel__foot-note">
          <span className="mono">{path}</span>
          <span aria-hidden="true">·</span>
          <span className="mono">
            {formatNumber(lines)} line{lines === 1 ? '' : 's'}
          </span>
        </span>
      }
    >
      {text.trim().length === 0 ? (
        <p className="state__body">
          This blob is empty. That is expected only when no site is registered yet.
        </p>
      ) : (
        <pre className="codepane mono" tabIndex={0} aria-label={`${title} contents`}>
          {text.replace(/\n+$/, '')}
        </pre>
      )}
    </Panel>
  );
}
